import { useCallback, useEffect, useRef } from "react";
import { MapRef, useMap } from "react-map-gl/maplibre";

import { useMapListener } from "@nens/react/hooks";
import { Extent } from "@nens/shared-utils";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { exportMapBlob } from "@/lib/rana-map-export/utils/export-map-to-screenshot";
import { usePublicationThumbnailQuery } from "@/queries/query-publication-thumbnail";

import { getId, usePublicationStore } from "../../../_internal";
import { PUBLICATION_MAP_ID } from "../../map/utils/map-id";
import { usePublicationBounds } from "../../map-layers";
import { useUpdatePublicationThumbnail } from "../api/update-publication-thumbnail";
import { TIMES_WIDTH_LARGER } from "../consts/times-width-larger";

const useTryToUpdateThumbnail = () => {
  const publicationId = usePublicationStore(getId);

  const { mutateAsync: uploadPublicationThumbnail } = useUpdatePublicationThumbnail();

  return useCallback(
    async ({ map, bounds }: { map: MapRef; bounds: Extent }) => {
      const height = 100;
      const width = height * TIMES_WIDTH_LARGER;
      const imageUpload = await exportMapBlob(
        map,
        { bounds },
        {
          width,
          height,
          format: "png",
        }
      );
      await uploadPublicationThumbnail({ publicationId, imageUpload });
    },
    [uploadPublicationThumbnail, publicationId]
  );
};

export const usePublicationThumbnail = () => {
  const { [PUBLICATION_MAP_ID]: map } = useMap();
  const publicationId = usePublicationStore(getId);
  const { data: publicationThumbnail, isSuccess } = usePublicationThumbnailQuery(publicationId);
  const { bounds, boundsLoading } = usePublicationBounds();
  const { canWriteProject } = useProjectActionAllowed();

  const tryToUpdateThumbnail = useTryToUpdateThumbnail();

  const thumbnailShouldBeUpdated = isSuccess && !publicationThumbnail;

  const stopUpload = useRef(false);

  const updateThumbnailIfNeeded = useCallback(async () => {
    // If the map is not completely loaded yet, we don't want to trigger an update because you will not see those layers in the thumbnail
    const isReadyForUpdate =
      !thumbnailShouldBeUpdated ||
      !canWriteProject ||
      !map ||
      boundsLoading ||
      !bounds ||
      !map.isStyleLoaded();

    if (isReadyForUpdate) return;

    if (stopUpload.current) return;
    stopUpload.current = true;

    try {
      await tryToUpdateThumbnail({ map, bounds });

      // Only set stopUpload to false if the upload was successful, otherwise we will keep trying to upload the thumbnail on every map idle event
      stopUpload.current = false;
    } catch (e) {
      console.error("Failed to upload publication thumbnail:", e);
    }
  }, [bounds, boundsLoading, canWriteProject, map, thumbnailShouldBeUpdated, tryToUpdateThumbnail]);

  // Call it with useEffect to so if thumbnailShouldBeUpdated switches to true the thumbnail will be updated immediately, instead of waiting for the next map idle event
  useEffect(() => {
    updateThumbnailIfNeeded();
  }, [updateThumbnailIfNeeded]);
  useMapListener("idle", updateThumbnailIfNeeded, map);
};
